import {useGlobalStore} from "@/stores/GlobalStore";
import NullOrUndefinedHelper from "@/utilities/helpers/NullOrUndefinedHelper";
import {getGermanIsoString} from "@/utilities/helpers/GermanIsoStringHelper";

export class FileOwnerInfo {
    userId: number = (useGlobalStore()).user?.id ?? -1;
    displayName: string = (useGlobalStore()).user?.displayName ?? '';
    email: string = (useGlobalStore()).user?.email ?? '';

    constructor(data: Record<string, any> | undefined = undefined) {
        Object.assign(this, data);
    }
}

export class FileDeletionInfo {
    scheduledDate?: string | null = null; //DateString
    reminderDate?: string | null = null; //DateString
    reminderSent: boolean = false;
    daysUntilDeletion: number = 0;
    canPostpone: boolean = true;

    constructor(data: Record<string, any> | undefined = undefined) {
        Object.assign(this, data);

        // when no scheduledDate value provided, set default from config
        if ((!data || NullOrUndefinedHelper(data.scheduledDate))) {
            const globalStore = useGlobalStore();
            const daysDefault = globalStore.settings.serviceConfig?.daysToDeleteFileDefault;

            if (daysDefault) {
                const today = new Date();
                const defaultDate = new Date(today);
                defaultDate.setDate(today.getDate() + daysDefault);
                this.scheduledDate = getGermanIsoString(defaultDate);
            }
        }
    }
}

export class RecipientOwnerInfo {
    userId: number = (useGlobalStore()).user?.id ?? -1;
    displayName: string = (useGlobalStore()).user?.displayName ?? '';

    constructor(data: Record<string, any> | undefined = undefined) {
        Object.assign(this, data);
    }
}

export class ListOwnerInfo {
    userId: number = (useGlobalStore()).user?.id ?? -1;
    displayName: string = (useGlobalStore()).user?.displayName ?? '';

    constructor(data: Record<string, any> | undefined = undefined) {
        Object.assign(this, data);
    }
}

export class RecipientSummary {
    recipientId: number = 0;
    displayName: string = '';
    email: string = '';
    initials: string = '';

    constructor(data: Record<string, any> | undefined = undefined) {
        Object.assign(this, data);
    }
}

export class RecipientListSummary {
    recipientlistID:number = 0;
    name:string = ''
    recipientCount:number = 0;
}

export class RetentionSettings {
    daysToKeepFiles: number = 0;
    daysToKeepLogs: number = 0;
    daysToKeepUnusedTickets: number = 0;
    filesRetentionDisplay: string = '';
    logsRetentionDisplay: string = '';
    ticketsRetentionDisplay: string = '';

    constructor(data: Record<string, any> | undefined = undefined) {
        Object.assign(this, data);
    }
}

export class TicketSettings {
    defaultValidityDays: number = 0;
    defaultAttempts: number = 0;
    daysBeforeDeletionReminder: number = 0;
    validityDisplay: string = '';
    attemptsDisplay: string = '';

    constructor(data: Record<string, any> | undefined = undefined) {
        Object.assign(this, data);
    }
}

export class SecuritySettings {
    otpValidityHours: number = 0;
    requireOTPForDownloads: boolean = true;
    requireOTPForUploads: boolean = true;
    otpValidityDisplay: string = '';

    constructor(data: Record<string, any> | undefined = undefined) {
        Object.assign(this, data);
    }
}

export class NotificationSettings {
    sendDownloadNotifications: boolean = false;
    sendUploadNotifications: boolean = false;
    sendDeletionReminders: boolean = false;
    reminderDaysBeforeDeletion: number = 0;

    constructor(data: Record<string, any> | undefined = undefined) {
        Object.assign(this, data);
    }
}

export class SystemLimits {
    maxFileSizeInBytes: number = 0;
    maxFileSizeDisplay: string = '';
    maxRecipientsPerList: number = 0;
    maxTicketsPerFile: number = 0;
    maxConcurrentUploads: number = 0;

    constructor(data: Record<string, any> | undefined = undefined) {
        Object.assign(this, data);
    }
}

export class ConfigModificationInfo {
    modifiedAt: string = '';
    modifiedBy: string = '';
    relativeTime: string = '';

    constructor(data: Record<string, any> | undefined = undefined) {
        Object.assign(this, data);
    }
}

export class TicketRecipientInfo {
    email: string = '';
    fullName?: string | null = null;
    isExistingUser: boolean = false;

    constructor(data: Record<string, any> | undefined = undefined) {
        Object.assign(this, data);
    }
}

export class TicketCreatorInfo {
    userId: number = (useGlobalStore()).user?.id ?? -1;
    displayName: string = (useGlobalStore()).user?.displayName ?? '';
    email: string = (useGlobalStore()).user?.email ?? '';

    constructor(data: Record<string, any> | undefined = undefined) {
        Object.assign(this, data);
    }
}

export class TicketValidityInfo {
    expiresAt: string = ""; //DateString
    isExpired: boolean = false;

    constructor(data: Record<string, any> | undefined = undefined) {
        Object.assign(this, data);

        // when no expiresAt value provided, set default from config
        if ((!data || NullOrUndefinedHelper(data.expiresAt))) {
            const globalStore = useGlobalStore();
            const daysDefault = globalStore.settings.serviceConfig?.daysToBeValidForTicketsDefault;

            if (daysDefault) {
                const today = new Date();
                const defaultDate = new Date(today);
                defaultDate.setDate(today.getDate() + daysDefault);
                this.expiresAt = getGermanIsoString(defaultDate);
            }
        }
    }
}

export class TicketSecurityInfo {
    attemptsRemaining: number = 0;
    totalAttempts: number = 0;

    constructor(data: Record<string, any> | undefined = undefined) {
        Object.assign(this, data);

        // set default if constructor doesnt contain a value for attemptsRemaining
        if (!data || !data.attemptsRemaining) {
            const globalStore = useGlobalStore();
            const attemptsDefault = globalStore.settings.serviceConfig?.defaultOfAttempts;

            if (attemptsDefault) {
                this.attemptsRemaining = attemptsDefault;
            }
        }
    }
}

export class UploadConfiguration {
    totalSizeInBytes: number = 0;
    displaySize: string = '';
    chunkSizeInBytes: number = 0;
    chunkDisplaySize: string = '';
    totalChunks: number = 0;

    constructor(data: Record<string, any> | undefined = undefined) {
        Object.assign(this, data);
    }
}

export class UploadProgress {
    chunksUploaded: number = 0;
    chunksRemaining: number = 0;
    uploadedChunkIndices: number[] = [];
    failedChunkIndices: number[] = [];
    percentComplete: number = 0;
    bytesUploaded: number = 0;
    displayBytesUploaded: string = '';
    currentSpeedMBps: number = 0;
    displaySpeed: string = '';
    estimatedTimeRemaining: string = '';
    displayTimeRemaining: string = '';

    constructor(data: Record<string, any> | undefined = undefined) {
        Object.assign(this, data);
    }
}

export class UploadTiming {
    startedAt: string = getGermanIsoString();
    completedAt?: string | null = null;
    pausedAt?: string | null = null;
    elapsedTime: string = '';
    displayElapsedTime: string = '';

    constructor(data: Record<string, any> | undefined = undefined) {
        Object.assign(this, data);
    }
}

export class UploadError {
    errorCode: string = '';
    message: string = '';
    occurredAt: string = getGermanIsoString();
    canRetry: boolean = false;

    constructor(data: Record<string, any> | undefined = undefined) {
        Object.assign(this, data);
    }
}

export class UserNotificationPreferences {
    downloadNotifications: boolean = false;
    uploadNotifications: boolean = false;
    deleteNotifications: boolean = false;
    mailSendNotifications: boolean = false;

    constructor(data: Record<string, any> | undefined = undefined) {
        Object.assign(this, data);
    }
}

export class TicketFileInfoResponse {
    ticketType?: number;
    fileName?: string;
    fileSize?: number;
    messageToRecipient?: string;
    creatorName?: string;
    validToDate?: string | null;
    attemptsRemaining?: number;

    constructor(data: Record<string, any> | undefined = undefined) {
        Object.assign(this, data)
    }
}

export class TicketFileInfo {
    fileID: number = 0;
    fileName: string = '';
    fileSize: number = 0;
    fileType: string = '';

    constructor(data: Record<string, any> | undefined = undefined) {
        Object.assign(this, data);
    }
}

export class TicketUploadedFileInfo {
    fileID: number = 0;
    fileName: string = '';
    fileSize: number = 0;
    fileType: string = '';
    messageFromRecipient: string = '';

    constructor(data: Record<string, any> | undefined = undefined) {
        Object.assign(this, data);
    }
}

export class RecipientToListAssociation {
    recipientListIds: number[] = [];

    constructor(data: Record<string, any> | undefined = undefined) {
        Object.assign(this, data);
    }
}

export class RecipientlistToRecipientAssociation {
    recipientIds: number[] = [];

    constructor(data: Record<string, any> | undefined = undefined) {
        Object.assign(this, data);
    }
}

